// ============================================================
// fondos/efectos/smoke.js — «Humo»: bocanadas que suben, crecen y se deshacen dejando estela (efecto de
// LIENZO a resolución BAJA, con ESTELA: el lienzo no se limpia entre pasos)
//
// Rehecho en la F3 del P3 (§3.1-bis, 30-09-2026). La clase de `vendor/bg/smoke-bg.js` subía 6-20
// manchas grandes y tendía un velo NEGRO al 8 % en cada fotograma (E2: la sección acababa gris
// oscura); en capas (29-09) sus penachos eran la misma nube que fluido y mesh («nula diferencia», el
// dueño el 30-09). La firma del humo es la TEXTURA, la SUBIDA y la ESTELA:
//   · bocanadas (10-36 según la densidad) de un solo color (el acento), cada una un sprite de ruido
//     fractal con jirones, que crece ×4 a lo largo de su vida y se desvanece;
//   · nacen ABAJO y suben frenando, con un vaivén que crece al subir y un viento común que las inclina
//     a la vez: el humo es denso abajo y se deshace arriba (el texto suele quedar en el tercio alto);
//   · la ESTELA: cada paso le quita a lo pintado un `DESVANECE` de su alfa (`destination-out`, como
//     matrix), así que al subir cada bocanada deja un rastro que se desvanece a TRANSPARENTE.
// Ese desvanecido MULTIPLICA el alfa, y el lienzo lo guarda en 8 bits: por debajo de 0,5/`DESVANECE`
// niveles un paso ya no lo baja, y lo que queda forma terrazas que se ven como anillos (medido el
// 30-09). Por eso cada `BARRIDO` pasos se le RESTA `RESIDUO` al alfa de todo el lienzo (`barrerResiduo`).
// Los sprites no giran: girarlos doblaba el coste en el hilo principal (9,3 % frente a 4,7 % medido,
// DPR 2, 1200×700).
// El lienzo va a ⅕ de la resolución de la página (como la clase de siempre) (`nitidez: 'baja'`): el humo no tiene filos.
//
// MÓDULO PURO: ES2019 sin `?.` ni `??`, sin DOM, sin reloj y sin estado de módulo.
// ============================================================

import { PASO, rgbDeBase, rgba, interpolar } from './comun.js';

/** Cuánto alfa le quita cada paso a lo ya pintado: la longitud de la estela. */
export const DESVANECE = 0.12;

/** Cada cuántos pasos se barre el residuo, y cuántos niveles de alfa (de 255) se restan al barrer. */
export const BARRIDO = 8;
export const RESIDUO = 4;

/** Lado (px) del sprite de una bocanada, y cuántos sprites distintos hay. */
const SPRITE = 96;
const VARIANTES = 4;

/** Octavas del ruido del sprite: lado de su rejilla y su peso. */
const OCTAVAS = [[3, 0.5], [6, 0.27], [12, 0.15], [24, 0.08]];

/** Cómo frena al subir (1/s): el humo sale con empuje y se queda flotando. */
const FRENO = 0.25;

/** Pasos como mucho por fotograma: tras un parón no se recupera el tiempo perdido de golpe. */
const MAX_PASOS = 8;

/** Una rejilla periódica de `k`×`k` valores al azar (ruido de valor sembrado). */
function rejilla(azar, k) {
    const v = [];
    for (let i = 0; i < k * k; i++) v.push(azar());
    return { k: k, v: v };
}

/** El ruido de `r` en (x, y) ∈ [0, 1): interpolación bilineal suavizada, periódica. */
function ruido(r, x, y) {
    const k = r.k;
    const fx = x * k;
    const fy = y * k;
    const x0 = Math.floor(fx);
    const y0 = Math.floor(fy);
    const tx = fx - x0;
    const ty = fy - y0;
    const sx = tx * tx * (3 - 2 * tx);
    const sy = ty * ty * (3 - 2 * ty);
    const i0 = ((x0 % k) + k) % k;
    const j0 = ((y0 % k) + k) % k;
    const i1 = (i0 + 1) % k;
    const j1 = (j0 + 1) % k;
    const a = r.v[j0 * k + i0];
    const b = r.v[j0 * k + i1];
    const c = r.v[j1 * k + i0];
    const d = r.v[j1 * k + i1];
    return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
}

/**
 * Un sprite de humo: ruido fractal (4 octavas) dentro de una máscara redonda, con HUECOS donde el ruido
 * baja: una bocanada con jirones, no una bola. La SUAVIDAD (0-100) cierra los huecos: a 100 es un
 * resplandor redondo.
 */
function spriteDeHumo(crearLienzo, azar, rgb, suavidad) {
    const s = (typeof suavidad === 'number' && suavidad > 0) ? Math.min(suavidad, 100) / 100 : 0;
    const N = SPRITE;
    const lienzo = crearLienzo(N, N);
    const c = lienzo.getContext('2d');
    const rejillas = OCTAVAS.map(function (o) { return rejilla(azar, o[0]); });
    const img = c.createImageData(N, N);
    const px = img.data;
    for (let y = 0; y < N; y++) {
        for (let x = 0; x < N; x++) {
            const u = (x + 0.5) / N;
            const v = (y + 0.5) / N;
            const dx = u - 0.5;
            const dy = v - 0.5;
            const r2 = 4 * (dx * dx + dy * dy);
            const i = (y * N + x) * 4;
            px[i] = rgb.r;
            px[i + 1] = rgb.g;
            px[i + 2] = rgb.b;
            if (r2 >= 1) { px[i + 3] = 0; continue; }
            let n = 0;
            for (let o = 0; o < OCTAVAS.length; o++) n += OCTAVAS[o][1] * ruido(rejillas[o], u, v);
            const densidad = Math.max(0, Math.min(1, (n - 0.3) / 0.45));
            const mascara = Math.pow(1 - r2, 1.8);
            px[i + 3] = Math.round(255 * mascara * (densidad * (1 - s) + s));
        }
    }
    c.putImageData(img, 0, 0);
    return lienzo;
}

/** Lo que ha subido una bocanada a los `seg` segundos de nacer (fracción de la sección). */
function subido(b, seg) {
    return b.sube * (1 - Math.exp(-FRENO * seg)) / FRENO;
}

/** Una bocanada nueva: nace abajo; al crear el efecto, ya en camino (su vida al azar, ya subida). */
function bocanada(s, azar, alCrear) {
    return {
        x: azar(),
        y0: 1.02 + azar() * 0.12,
        vida: alCrear ? azar() : 0,
        dura: (6 + azar() * 4) / s.vf,                       // s
        sube: (0.13 + azar() * 0.09) * s.vf,                 // empuje inicial: fracción de la sección por segundo
        r0: 0.07 + azar() * 0.05,                            // × el lado menor
        vaiven: 0.01 + azar() * 0.015,                       // × el ancho; crece al subir
        ritmo: (0.4 + azar() * 0.5) * s.vf,                  // rad/s
        fase: azar() * Math.PI * 2,
        sprite: Math.floor(azar() * VARIANTES),
    };
}

/** Sube de golpe, se deshace despacio. */
function envolvente(vida) {
    if (vida < 0.1) return vida / 0.1;
    return Math.pow(1 - (vida - 0.1) / 0.9, 1.3);
}

/**
 * Resta `RESIDUO`/255 al alfa de TODO el lienzo. Restar una constante no existe como composición, pero
 * invertir sí: con un relleno opaco, `xor` deja 1 − α con el color del relleno (el del humo, que es
 * monocromo: no se pierde nada), `lighter` suma ε y el segundo `xor` vuelve a invertir → α − ε (0 si
 * no llegaba). En 8 bits es aritmética exacta: 255 − (255 − a + ε) = a − ε. Exportado para los tests.
 */
export function barrerResiduo(s, ctx) {
    const opaco = `rgb(${s.rgb.r}, ${s.rgb.g}, ${s.rgb.b})`;
    ctx.globalCompositeOperation = 'xor';
    ctx.fillStyle = opaco;
    ctx.fillRect(0, 0, s.w, s.h);
    ctx.globalCompositeOperation = 'lighter';
    ctx.fillStyle = rgba(s.rgb, RESIDUO / 255);
    ctx.fillRect(0, 0, s.w, s.h);
    ctx.globalCompositeOperation = 'xor';
    ctx.fillStyle = opaco;
    ctx.fillRect(0, 0, s.w, s.h);
    ctx.globalCompositeOperation = 'source-over';
}

/** UN paso del humo: la estela se desvanece (a TRANSPARENTE) y cada bocanada avanza y se pinta. */
export function pasoDeHumo(s, ctx) {
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = `rgba(0, 0, 0, ${DESVANECE})`;
    ctx.fillRect(0, 0, s.w, s.h);
    ctx.globalCompositeOperation = 'source-over';
    s.pasos++;
    if (s.pasos % BARRIDO === 0) barrerResiduo(s, ctx);
    const seg = PASO / 1000;
    s.t += seg;
    const minDim = Math.min(s.w, s.h);
    // Un viento común que las inclina a la vez.
    const viento = 0.02 * Math.sin(s.t * 0.17 * s.vf) + 0.01 * Math.sin(s.t * 0.43 * s.vf + 1);
    for (let i = 0; i < s.bocanadas.length; i++) {
        let b = s.bocanadas[i];
        b.vida += seg / b.dura;
        if (b.vida >= 1) b = s.bocanadas[i] = bocanada(s, s.azar, false);
        const px = (b.x + viento * b.vida + b.vaiven * (0.3 + 1.4 * b.vida) * Math.sin(s.t * b.ritmo + b.fase)) * s.w;
        const py = (b.y0 - subido(b, b.vida * b.dura)) * s.h;
        const r = b.r0 * minDim * (1 + 3 * b.vida);
        ctx.globalAlpha = s.alfa * envolvente(b.vida);
        ctx.drawImage(s.sprites[b.sprite], px - r, py - r, r * 2, r * 2);
    }
    ctx.globalAlpha = 1;
}

export default {
    nitidez: 'baja',
    divisor: 5,
    estela: true,

    /** Para el fotograma QUIETO: lo que tarda la estela en formarse. */
    precalentado: 3000,

    crear(ctx, e) {
        const p = e.params;
        const n = Math.round(interpolar(p.density, 5, e.tactil ? 6 : 10, e.tactil ? 18 : 36));
        // Velocidad 1-10 → 0,3×-2,5× (la escala de siempre).
        const vf = 0.3 + ((p.speed != null ? p.speed : 5) - 1) * (2.2 / 9);
        const rgb = rgbDeBase(e.c1);
        const sprites = [];
        for (let i = 0; i < VARIANTES; i++) sprites.push(spriteDeHumo(e.crearLienzo, e.azar, rgb, p.blur));
        const s = { w: e.w, h: e.h, azar: e.azar, alfa: e.alfa, rgb: rgb, vf: vf, t: 0, pasos: 0,
                    desde: PASO, sprites: sprites, bocanadas: [] };
        for (let i = 0; i < n; i++) s.bocanadas.push(bocanada(s, e.azar, true));
        return s;
    },

    /** Re-medirse deja el lienzo en blanco (lo hace el navegador): la estela vuelve a formarse. */
    redimensionar(s, w, h) {
        s.w = w;
        s.h = h;
    },

    dibujar(s, ctx, t, dt) {
        if (!(dt > 0)) return;
        s.desde += dt;
        let n = 0;
        while (s.desde >= PASO && n < MAX_PASOS) {
            pasoDeHumo(s, ctx);
            s.desde -= PASO;
            n++;
        }
        if (s.desde >= PASO) s.desde = 0;
    },
};
